import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";

/**
 * Global listener for backend 402 plan-gate errors.
 * Any api call that returns a structured plan-gate payload will dispatch a
 * `yuo:plan-gate` CustomEvent, which we render as a soft toast with an
 * "Upgrade" CTA that lands on /paywall pre-focused on the next tier.
 *
 * Auto-hides after 8 seconds. Manual dismiss with the × button.
 */
export function PlanGateToast() {
  const [gate, setGate] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => setGate(e.detail || null);
    window.addEventListener("yuo:plan-gate", handler);
    return () => window.removeEventListener("yuo:plan-gate", handler);
  }, []);

  useEffect(() => {
    if (!gate) return;
    const t = setTimeout(() => setGate(null), 8000);
    return () => clearTimeout(t);
  }, [gate]);

  if (!gate) return null;

  const lang = "en";
  const msg = lang === "ru" ? gate.message_ru : gate.message_en;
  const cta = "Upgrade";

  return (
    <div
      data-testid="plan-gate-toast"
      style={{
        position: "fixed",
        bottom: 100,
        left: "50%",
        transform: "translateX(-50%)",
        maxWidth: 520,
        width: "calc(100% - 32px)",
        background: "#1B1B2C",
        border: `1px solid rgba(var(--role-yellow-rgb),0.40)`,
        borderRadius: 20,
        padding: "16px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        zIndex: 9998,
        boxShadow: `0 12px 40px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(var(--role-yellow-rgb),0.13), 0 0 32px rgba(var(--role-yellow-rgb),0.13)`,
        fontFamily: FONTS.body,
        animation: "yuoRise 380ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div style={{ flex: 1, color: COLORS.textPrimary, fontSize: 14, lineHeight: "20px" }}>
        {msg}
      </div>
      <button
        onClick={() => {
          const next = gate.next_plan || "pro";
          setGate(null);
          navigate(`/paywall?plan=${next}&feature=${encodeURIComponent(gate.feature || "")}`);
        }}
        data-testid="plan-gate-upgrade"
        style={{
          padding: "8px 16px",
          borderRadius: 999,
          background: COLORS.library,
          color: "#0D0D1A",
          fontFamily: FONTS.body,
          fontSize: 13,
          fontWeight: 700,
          border: "none",
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        {cta} →
      </button>
      <button
        onClick={() => setGate(null)}
        data-testid="plan-gate-dismiss"
        aria-label="Dismiss"
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          border: `1px solid ${COLORS.border}`,
          background: "transparent",
          color: COLORS.textMuted,
          fontSize: 14,
          cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>
  );
}
